import React, { useState, useEffect, useCallback } from 'react'
import { apiClient } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import type { BCCWJMemoryStatus, BCCWJWord, Deck } from '../types'
import { BCCWJ_MEMORY_STATUS_LABELS } from '../constants/bccwjStatus'
import './WordTriage.css'

const LEVELS: BCCWJMemoryStatus[] = [1, 2, 3, 4]

type TriagePhase = 'setup' | 'triaging' | 'summary'

interface SessionStats {
  levels: Record<BCCWJMemoryStatus, number>
  added: number
}

const WordTriage: React.FC = () => {
  const { user } = useAuth()

  // Phase
  const [phase, setPhase] = useState<TriagePhase>('setup')

  // Setup state
  const [decks, setDecks] = useState<Deck[]>([])
  const [selectedDeckId, setSelectedDeckId] = useState('')
  const [rankMin, setRankMin] = useState(1)
  const [rankMax, setRankMax] = useState(1000)
  const [selectedPos, setSelectedPos] = useState('')
  const [posTypes, setPosTypes] = useState<string[]>([])
  const [setupLoading, setSetupLoading] = useState(true)

  // Triage state
  const [currentWord, setCurrentWord] = useState<BCCWJWord | null>(null)
  const [totalRemaining, setTotalRemaining] = useState(0)
  const [currentOffset, setCurrentOffset] = useState(0)
  const [triageLoading, setTriageLoading] = useState(false)
  const [stats, setStats] = useState<SessionStats>({ levels: { 1: 0, 2: 0, 3: 0, 4: 0 }, added: 0 })

  // Load setup data
  useEffect(() => {
    const loadSetup = async () => {
      try {
        const [decksRes, posRes] = await Promise.all([
          apiClient.getDecks(),
          apiClient.getBCCWJPosTypes(),
        ])
        const deckList = (decksRes as any)?.data || decksRes || []
        setDecks(Array.isArray(deckList) ? deckList : [])
        setPosTypes(posRes)
      } catch (err) {
        console.error('Failed to load setup data:', err)
      } finally {
        setSetupLoading(false)
      }
    }
    loadSetup()
  }, [user?.user_id])

  // Load next word for triage
  const loadNextWord = useCallback(async (offset: number) => {
    setTriageLoading(true)
    try {
      const result = await apiClient.getTriageWords(
        rankMin,
        rankMax,
        selectedPos || undefined,
        1,
        offset
      )
      setCurrentWord(result.word)
      setTotalRemaining(result.total_remaining)
      if (!result.word) {
        setPhase('summary')
      }
    } catch (err) {
      console.error('Failed to load triage word:', err)
    } finally {
      setTriageLoading(false)
    }
  }, [rankMin, rankMax, selectedPos])

  // Start triage session
  const startTriage = async () => {
    if (!selectedDeckId) return
    setStats({ levels: { 1: 0, 2: 0, 3: 0, 4: 0 }, added: 0 })
    setCurrentOffset(0)
    setPhase('triaging')
    await loadNextWord(0)
  }

  // Handle a memory-level tap: set status, then advance to the next word
  const handleLevel = async (level: BCCWJMemoryStatus) => {
    if (!currentWord || triageLoading) return
    setTriageLoading(true)
    try {
      await apiClient.setBCCWJWordStatus(currentWord.id, level)
      const newOffset = currentOffset + 1
      setCurrentOffset(newOffset)
      setStats(prev => ({ ...prev, levels: { ...prev.levels, [level]: prev.levels[level] + 1 } }))
      await loadNextWord(newOffset)
    } catch (err) {
      console.error('Failed to set word status:', err)
      setTriageLoading(false)
    }
  }

  // Handle "Don't know it" → add to deck
  const handleDontKnow = async () => {
    if (!currentWord || triageLoading) return
    setTriageLoading(true)
    try {
      await apiClient.createCardFromBCCWJ(selectedDeckId, currentWord.id)
    } catch (err: any) {
      // 409 = the endpoint's duplicate guard: the word is already in the
      // deck, which counts as added — advance instead of erroring.
      if (err?.response?.status !== 409) {
        console.error('Failed to create card:', err)
        setTriageLoading(false)
        return
      }
    }
    const newOffset = currentOffset + 1
    setCurrentOffset(newOffset)
    setStats(prev => ({ ...prev, added: prev.added + 1 }))
    await loadNextWord(newOffset)
  }

  // Keyboard shortcuts
  useEffect(() => {
    if (phase !== 'triaging' || triageLoading) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't hijack browser/OS shortcuts (Cmd+1 tab switch, etc.)
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key >= '1' && e.key <= '4') {
        e.preventDefault()
        handleLevel(Number(e.key) as BCCWJMemoryStatus)
      } else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        e.preventDefault()
        handleDontKnow()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setPhase('summary')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [phase, triageLoading, currentWord, selectedDeckId, currentOffset, rankMin, rankMax, selectedPos])

  // ==================== SETUP PHASE ====================
  if (phase === 'setup') {
    return (
      <div className="triage-container">
        <h2 className="triage-title">単語トリアージ</h2>
        <p className="triage-subtitle">単語をすばやく絞り込んでデッキに追加</p>

        {setupLoading ? (
          <div className="triage-loading">読み込み中...</div>
        ) : (
          <div className="triage-setup">
            <div className="triage-form-group">
              <label htmlFor="deck-select">対象デッキ</label>
              <select
                id="deck-select"
                value={selectedDeckId}
                onChange={e => setSelectedDeckId(e.target.value)}
              >
                <option value="">-- デッキを選択 --</option>
                {decks.map(deck => (
                  <option key={deck.deck_id} value={deck.deck_id}>
                    {deck.name} ({deck.card_count} 枚)
                  </option>
                ))}
              </select>
            </div>

            <div className="triage-form-row">
              <div className="triage-form-group">
                <label htmlFor="rank-min">順位の下限</label>
                <input
                  id="rank-min"
                  type="number"
                  min={1}
                  value={rankMin}
                  onChange={e => setRankMin(Number(e.target.value))}
                />
              </div>
              <div className="triage-form-group">
                <label htmlFor="rank-max">順位の上限</label>
                <input
                  id="rank-max"
                  type="number"
                  min={1}
                  value={rankMax}
                  onChange={e => setRankMax(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="triage-form-group">
              <label htmlFor="pos-select">品詞</label>
              <select
                id="pos-select"
                value={selectedPos}
                onChange={e => setSelectedPos(e.target.value)}
              >
                <option value="">すべて</option>
                {posTypes.map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>

            <button
              className="triage-start-btn"
              onClick={startTriage}
              disabled={!selectedDeckId}
            >
              トリアージを開始
            </button>
          </div>
        )}
      </div>
    )
  }

  // ==================== TRIAGE PHASE ====================
  if (phase === 'triaging') {
    return (
      <div className="triage-container">
        <div className="triage-header">
          <div className="triage-progress">
            <span>残り約 {totalRemaining} 語</span>
            <span className="triage-stats-inline">
              {LEVELS.map(l => `${l}:${stats.levels[l]}`).join(' ')} | 追加: {stats.added}
            </span>
          </div>
          <button className="triage-end-btn" onClick={() => setPhase('summary')}>
            セッションを終了
          </button>
        </div>

        {triageLoading ? (
          <div className="triage-loading">
            <div className="triage-spinner" />
          </div>
        ) : currentWord ? (
          <div className="triage-card-area">
            <div className="triage-word-card">
              <div className="triage-word-rank">#{currentWord.rank}</div>
              <div className="triage-word-lemma">{currentWord.lemma}</div>
              {currentWord.hiragana && currentWord.hiragana !== currentWord.lemma && (
                <div className="triage-word-reading">{currentWord.hiragana}</div>
              )}
              {currentWord.pos && (
                <div className="triage-word-pos">{currentWord.pos}</div>
              )}
              {currentWord.frequency && (
                <div className="triage-word-freq">
                  頻度: {currentWord.frequency.toLocaleString()}
                  {currentWord.pmw && ` (100万分の${currentWord.pmw.toFixed(1)})`}
                </div>
              )}
            </div>

            <div className="triage-actions triage-level-actions">
              {LEVELS.map(level => (
                <button
                  key={level}
                  className={`triage-btn triage-btn-level triage-btn-level-${level}`}
                  onClick={() => handleLevel(level)}
                  disabled={triageLoading}
                >
                  {BCCWJ_MEMORY_STATUS_LABELS[level]}
                  <span className="triage-btn-shortcut">({level})</span>
                </button>
              ))}
              <button
                className="triage-btn triage-btn-add"
                onClick={handleDontKnow}
                disabled={triageLoading}
              >
                デッキに追加
                <span className="triage-btn-shortcut">(→ / D)</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="triage-empty">選択した条件に一致する単語が見つかりません。</div>
        )}
      </div>
    )
  }

  // ==================== SUMMARY PHASE ====================
  return (
    <div className="triage-container">
      <h2 className="triage-title">セッション完了</h2>
      <div className="triage-summary">
        {LEVELS.map(level => (
          <div className="triage-summary-stat" key={level}>
            <span className="triage-summary-label">{BCCWJ_MEMORY_STATUS_LABELS[level]}</span>
            <span className="triage-summary-value triage-known-count">{stats.levels[level]}</span>
          </div>
        ))}
        <div className="triage-summary-stat">
          <span className="triage-summary-label">デッキに追加した単語</span>
          <span className="triage-summary-value triage-added-count">{stats.added}</span>
        </div>
        <div className="triage-summary-stat">
          <span className="triage-summary-label">仕分けた合計</span>
          <span className="triage-summary-value">
            {LEVELS.reduce((sum, l) => sum + stats.levels[l], 0) + stats.added}
          </span>
        </div>
      </div>
      <div className="triage-summary-actions">
        <button className="triage-start-btn" onClick={() => setPhase('setup')}>
          新しいセッション
        </button>
      </div>
    </div>
  )
}

export default WordTriage